import { zodResolver } from "@hookform/resolvers/zod";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { correctionSchema, MAX_REASON_LENGTH, MAX_TOKENS } from "./tokensSchema";
import { useCorrectBalance } from "../../api/useCaptainBillingMutations";
import type { CaptainBillingAccount } from "../../types";
import { topUpAfterCorrection } from "../../utils";

import { Button } from "@/components/ui/v2/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import { Input } from "@/components/ui/v2/input";
import { SegmentedControl } from "@/components/ui/v2/segmented-control";
import { cn } from "@/lib/utils/cn";
import { useValueFormatter } from "@/utils/useValueFormatter";

type CorrectionSchema = ReturnType<typeof correctionSchema>;
type Direction = "add" | "remove";

type CorrectionEditorProps = {
  account: CaptainBillingAccount;
  onDone: () => void;
};

export function CorrectionEditor({ account, onDone }: CorrectionEditorProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const { save, isPending } = useCorrectBalance();
  const [direction, setDirection] = useState<Direction>("remove");
  const form = useForm<z.input<CorrectionSchema>, unknown, z.output<CorrectionSchema>>({
    resolver: zodResolver(correctionSchema(t)),
    defaultValues: { tokens: null, reason: "" },
  });

  const sign = direction === "add" ? 1 : -1;
  const amount = Math.max(0, form.watch("tokens") ?? 0);
  const requested = sign * amount;
  const after = topUpAfterCorrection(account.topUpBalanceTokens, requested);
  const clamped = account.topUpBalanceTokens + requested < 0;

  const onSubmit = ({ tokens, reason }: z.output<CorrectionSchema>) =>
    save({ clientId: account.clientId, tokens: sign * tokens, reason }, form.setError, onDone);

  const tokensError = form.formState.errors.tokens?.message;
  const reasonError = form.formState.errors.reason?.message;
  const preview = t(clamped ? "captainBilling.editors.correct.afterClamped" : "captainBilling.editors.correct.after", {
    tokens: format(after, "decimal"),
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !isPending) onDone();
        }}
        noValidate
        className="flex flex-col gap-1.5"
      >
        <div className="flex items-center gap-2">
          <FormField
            control={form.control}
            name="tokens"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1">
                <FormControl>
                  <FormattedCurrencyInput
                    aria-label={t("captainBilling.editors.correct.amount")}
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    onBlur={field.onBlur}
                    prefix={direction === "add" ? "+" : "−"}
                    suffix={t("captainBilling.editors.unit")}
                    maxValue={MAX_TOKENS}
                    inputMode="numeric"
                    placeholder="0"
                    disabled={isPending}
                    autoFocus
                    className="h-9 text-base font-semibold tabular-nums"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <SegmentedControl
            label={t("captainBilling.editors.correct.direction")}
            value={direction}
            onValueChange={setDirection}
            options={[
              { value: "remove", label: <DirectionIcon icon={Minus} label={t("captainBilling.editors.correct.remove")} /> },
              { value: "add", label: <DirectionIcon icon={Plus} label={t("captainBilling.editors.correct.add")} /> },
            ]}
            disabled={isPending}
            className="shrink-0"
            itemClassName="min-w-8 px-2"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1">
                <FormControl>
                  <Input
                    {...field}
                    aria-label={t("captainBilling.editors.correct.reasonLabel")}
                    placeholder={t("captainBilling.editors.correct.reasonPlaceholder")}
                    maxLength={MAX_REASON_LENGTH}
                    disabled={isPending}
                    className="h-9"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <p
            role={tokensError || reasonError ? "alert" : undefined}
            className={cn(
              "min-w-0 text-sm tabular-nums",
              tokensError || reasonError ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {tokensError ?? reasonError ?? preview}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={onDone} disabled={isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" loading={isPending}>
              {t("captainBilling.editors.correct.save")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function DirectionIcon({ icon: Icon, label }: { icon: typeof Plus; label: string }) {
  return (
    <span className="flex items-center" title={label}>
      <Icon className="size-4" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}